import React, {useState} from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {sortArrayByProperty} from 'helpers/array-helper';
import {getText} from 'helpers/text-helper';
import {localizationData} from 'data/localization-data';
import {themesData} from 'data/themes-data';
import DatePicker, { registerLocale } from 'react-datepicker';
import Button from 'components/ui/button/button';
import './stats.scss';

const Stats = ({games, archivedGames, groups, goToPage}) => {
	/* Localize dates */
	registerLocale('da', {
		localize: {
			month: (n) => {return localizationData.months[n];},
			day: (n) => {return localizationData.days[n];}
		}, 
		formatLong:{
			date: () => {return 'yyyy-mm-dd';}
		} 
	});

	/* Combine games and archived games */
	const allGames = games.concat(archivedGames);

	/* Combine groups and archived groups */
	const allGroups = JSON.parse(JSON.stringify(groups));
	archivedGames.forEach((g) => {
		if (g.players) {
			g.players.forEach((p) => {
				allGroups.push(p);
			});
		}
	});

	/* Unique facilitators */
	const facilitators = [];
	allGames.forEach((game) => {
		const facilitatorIndex = facilitators.findIndex((f) => {return f.id === game.facilitatorId;});
		if (facilitatorIndex < 0) {
			facilitators.push({id: game.facilitatorId, created: game.created});
		} else {
			if (game.created < facilitators[facilitatorIndex].created) {
				facilitators[facilitatorIndex].created = game.created;
			}
		}
	});

	/* Get earliest creation date */
	const firstDate = (allGames.length > 0 
		? dayjs(sortArrayByProperty(allGames, 'created', 'ASC')[0].created).format('YYYYMMDD')
		: dayjs(new Date()).format('YYYYMMDD')
	);

	/* Start and end dates */
	const [startDate, setStartDate] = useState(firstDate);
	const [endDate, setEndDate] = useState(dayjs(new Date()).format('YYYYMMDD'));

	/* Filter by dates */ 
	const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
	const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
	const filteredGames = allGames.filter((g) => {
		return (
			g.hasOwnProperty('created') && 
			g.created >= formattedStartDate &&
			g.created <= formattedEndDate
		);
	});
	const filteredFacilitators = facilitators.filter((f) => {
		return (
			f.hasOwnProperty('created') && 
			f.created >= formattedStartDate &&
			f.created <= formattedEndDate
		);
	});
	const filteredGroups = allGroups.filter((g) => {
		return (
			g.hasOwnProperty('created') && 
			g.created >= formattedStartDate &&
			g.created <= formattedEndDate
		);
	});


	/* Get game stats */
	const totalStarted = filteredGames.length;
	const totalCompleted = filteredGames.filter((g) => {
		return (
			g.players &&
			g.players.length > 0 &&
			g.players.some((p) => {return p.currentGameStepId && p.currentGameStepId.includes('finish');})
		);
	}).length;
	const totalCompletedPercent = (totalStarted > 0
		? Math.round(((totalCompleted / totalStarted) * 100.))
		: 0
	);

	/* Get group stats */
	const totalGroups = filteredGroups.length;
	const completedGroups = filteredGroups.filter((g) => {
		return (
			g.currentGameStepId && g.currentGameStepId.includes('finish')
		);
	}).length;
	const completedGroupsPercent = (totalGroups > 0
		? Math.round(((completedGroups / totalGroups) * 100.))
		: 0
	);
	

	return (
		<div className="Stats">
			<div className="Stats-backBtn">
				<Button 
					classes={['pink', 'fullHeight']}
					text={getText('statsTexts', 'backBtn')} 
					onClick={() => {goToPage('settings');}} 
				/>
			</div>
			<div className="Stats-title">{getText('statsTexts', 'stats')}</div>
			<div className="Stats-period">
				<DatePicker
					disabled={false}
					selected={startDate ? dayjs(startDate).toDate() : new Date()}
					onChange={(date) => {setStartDate(dayjs(date).format('YYYYMMDD'));}}
					locale={'da'}
					dateFormat="dd/MM-yyyy"
					placeholderText="dd/MM-yyyy"
				/>
				<div className="Stats-periodTo"><span>{getText('statsTexts', 'to')}</span></div>
				<DatePicker
					disabled={false}
					selected={endDate ? dayjs(endDate).toDate() : new Date()}
					onChange={(date) => {setEndDate(dayjs(date).format('YYYYMMDD'));}}
					locale={'da'}
					dateFormat="dd/MM-yyyy"
					placeholderText="dd/MM-yyyy"
				/>
			</div>

			{/* General stats */}
			<div className="Stats-section">
				<div className="Stats-columns">
					<div className="Stats-column">
						<div className="Stats-sectionRow">
							<span>{getText('statsTexts', 'facilitators')}:</span>
							<span>{filteredFacilitators.length}</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('statsTexts', 'startedGames')}:</span><span>{totalStarted}</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('statsTexts', 'finishedGames')}:</span>
							<span>{totalCompleted} ({totalCompletedPercent}%)</span>
						</div>
					</div>
					<div className="Stats-column">
						<div className="Stats-sectionRow"><span></span></div>
						<div className="Stats-sectionRow">
							<span>{getText('statsTexts', 'startedGroups')}:</span><span>{totalGroups}</span>
						</div>
						<div className="Stats-sectionRow">
							<span>{getText('statsTexts', 'finishedGroups')}:</span>
							<span>{completedGroups} ({completedGroupsPercent}%)</span>
						</div>
					</div>
				</div>
			</div>
			<div className="Stats-section">
				<div className="Stats-themes">
					<div className="Stats-subtitle">
						<span>{getText('statsTexts', 'themes')}</span>
					</div>
					{themesData.map((theme, index) => {
						const numberOfGames = filteredGames.filter((g) => {return g.themeId === theme.id;}).length;
						const percentGames = (totalStarted > 0
							? Math.round(((numberOfGames / totalStarted) * 100.))
							: 0
						);
						return (
							<div key={index} className="Stats-sectionRow">
								<span>{getText('statsTexts', 'theme')} {(index + 1)}: </span>
								<span>{numberOfGames} ({percentGames}%)</span>
							</div>
						);
					})}
				</div>				
			</div>
		</div>
	);
};

Stats.propTypes = {
	games: PropTypes.array.isRequired,
	archivedGames: PropTypes.array.isRequired,
	groups: PropTypes.array.isRequired,
	goToPage: PropTypes.func.isRequired,
};

export default Stats;